/**
 * A requisition's postings — `GET`/`POST /recruiting/requisitions/{id}/postings`.
 *
 * The list is a bare `{ data: [...] }` (no page, no cursor), newest first, so it is
 * read whole and there is no partial-data case. A posting is created in `draft`; the
 * server owns the status, so this form offers `channel`, an optional `public_slug`
 * and an optional `expires_at` and nothing else.
 *
 * `public_slug` must match `^[a-z0-9-]+$` and is unique per company among **published**
 * postings — a collision answers `409 WORKFORCE_CONFLICT`, which is rendered as the
 * server's own sentence rather than pre-judged here, because "published" is a fact
 * this screen cannot see about other requisitions. The pattern is enforced before
 * sending only as a hint; the server's `400` is the authority.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  PostingChannel,
  PostingStatus,
  RecruitingPosting,
  RecruitingPostingCreateRequest,
} from '../../api/recruiting';
import { POSTING_CHANNELS } from '../../api/recruiting';
import type { WorkforceApiError } from '../../api/errors';
import type { QueryStatus } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { SEVERITY_ICON, type Severity } from '../../states/icons';

const SLUG_PATTERN = /^[a-z0-9-]+$/;

/** How loud a posting's status is — active is quiet-good, expired wants a second look. */
const POSTING_STATUS_BADGE: Readonly<Record<PostingStatus, Severity>> = {
  draft: 'info',
  published: 'low',
  unpublished: 'medium',
  expired: 'medium',
};

export interface PostingsPanelProps {
  readonly status: QueryStatus;
  readonly postings: readonly RecruitingPosting[] | null;
  readonly readError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly timezone: string;
  readonly createPending: boolean;
  readonly createError: WorkforceApiError | null;
  readonly created: RecruitingPosting | null;
  readonly onCreate: (body: RecruitingPostingCreateRequest) => void;
}

export function PostingsPanel({
  status,
  postings,
  readError,
  onReload,
  timezone,
  createPending,
  createError,
  created,
  onCreate,
}: PostingsPanelProps): ReactNode {
  const { t, locale } = useI18n();
  const [channel, setChannel] = useState<'' | PostingChannel>('');
  const [slug, setSlug] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const slugTrimmed = slug.trim();
  const slugValid = slugTrimmed === '' || SLUG_PATTERN.test(slugTrimmed);

  const build = (): RecruitingPostingCreateRequest => ({
    ...(channel === '' ? {} : { channel }),
    ...(slugTrimmed === '' ? {} : { public_slug: slugTrimmed }),
    ...(expiresAt.trim() === '' ? {} : { expires_at: new Date(expiresAt).toISOString() }),
  });

  return (
    <section className="wp-section" aria-labelledby="w1-postings-heading" data-testid="w1-postings">
      <h3 className="wp-section__title" id="w1-postings-heading">
        {t('recruiting.jobs.postings.title')}
      </h3>
      <p className="wp-small wp-muted">{t('recruiting.jobs.postings.intro')}</p>

      {status === 'loading' ? (
        <Skeleton
          rows={2}
          label={t('recruiting.jobs.postings.subject')}
          testId="w1-postings-skeleton"
        />
      ) : null}
      {status === 'error' && readError !== null ? (
        <ApiErrorNote
          error={readError}
          onRetry={onReload}
          subject={t('recruiting.jobs.postings.subject')}
          testId="w1-postings-error"
        />
      ) : null}

      {postings === null ? null : postings.length === 0 ? (
        <EmptyState
          title={t('recruiting.jobs.postings.empty.title')}
          description={t('recruiting.jobs.postings.empty.body')}
          testId="w1-postings-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w1-posting-list">
          {postings.map((posting) => (
            <li className="wp-row" key={posting.id} data-testid={`w1-posting-${posting.id}`}>
              <div className="wp-row__main">
                <p>
                  <Badge
                    severity={POSTING_STATUS_BADGE[posting.status]}
                    icon={SEVERITY_ICON[POSTING_STATUS_BADGE[posting.status]]}
                    label={t(`recruiting.jobs.postings.status.${posting.status}`)}
                    testId={`w1-posting-status-${posting.id}`}
                  />{' '}
                  <span>
                    {posting.channel === null
                      ? t('recruiting.jobs.postings.noChannel')
                      : t(`recruiting.jobs.postings.channel.${posting.channel}`)}
                  </span>
                </p>
                <p className="wp-mono">
                  {posting.public_slug ?? t('recruiting.jobs.postings.noSlug')}
                </p>
                <p className="wp-small wp-muted">
                  {posting.published_at === null
                    ? t('recruiting.jobs.postings.notPublished')
                    : t('recruiting.jobs.postings.publishedAt', {
                        when: formatInstant(posting.published_at, locale, timezone),
                      })}
                  {posting.expires_at === null
                    ? ''
                    : ` · ${t('recruiting.jobs.postings.expiresAt', {
                        when: formatInstant(posting.expires_at, locale, timezone),
                      })}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (createPending || !slugValid) return;
          onCreate(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-posting-channel">
            {t('recruiting.jobs.postings.channel')}
          </label>
          <select
            className="wp-field__input"
            id="w1-posting-channel"
            data-testid="w1-posting-channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value as '' | PostingChannel);
            }}
          >
            <option value="">{t('recruiting.jobs.postings.noChannel')}</option>
            {POSTING_CHANNELS.map((option) => (
              <option key={option} value={option}>
                {t(`recruiting.jobs.postings.channel.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-posting-slug">
            {t('recruiting.jobs.postings.slug')}
          </label>
          <input
            className="wp-field__input"
            id="w1-posting-slug"
            data-testid="w1-posting-slug"
            type="text"
            value={slug}
            autoComplete="off"
            onChange={(event) => {
              setSlug(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.jobs.postings.slugRule')}</p>
          {slugValid ? null : (
            <p className="wp-small" data-testid="w1-posting-slug-invalid">
              {t('recruiting.jobs.postings.slugInvalid')}
            </p>
          )}
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-posting-expires">
            {t('recruiting.jobs.postings.expires')}
          </label>
          <input
            className="wp-field__input"
            id="w1-posting-expires"
            data-testid="w1-posting-expires"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => {
              setExpiresAt(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w1-posting-create"
            disabled={createPending || !slugValid}
          >
            {createPending
              ? t('recruiting.jobs.postings.creating')
              : t('recruiting.jobs.postings.create')}
          </button>
        </div>
      </form>

      {createError === null ? null : (
        <ApiErrorNote
          error={createError}
          subject={t('recruiting.jobs.postings.subject')}
          testId="w1-posting-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.jobs.postings.ok')}
          testId="w1-posting-ok"
        />
      )}
    </section>
  );
}
